/**
 * An admin study's child studies, as `GET study/:id/child-studies` sends them (spdata-api#568,
 * "Client contract", with the case counts spdata-api#596 adds): counts and completion per child
 * study, never an entry, a field value, a date or an id of a case or entry. One child study's cases
 * come from a second read, `GET study/:id/child-studies/:childId/cases` (spdata-api#596), when its
 * block opens.
 *
 * The types are the contract's shape, and the section draws from the same shape. Both mappers copy
 * each listed field by name and nothing else, so a key the server adds later never reaches a
 * template (ENG-9642).
 */

export interface ChildStudyForm {
  id: string;
  title: string;
  entryCount: number;
  /** Cases with an entry on the form. */
  casesWithEntry: number;
  /** Of those, the cases whose record on the form is complete; null when completion is not measured. */
  casesComplete: number | null;
}

/**
 * Finished or active cases, and how many of them have a started form still incomplete (null when
 * completion is not measured).
 */
export interface ChildStudyCaseSplit {
  all: number;
  incomplete: number | null;
}

export interface ChildStudy {
  id: string;
  title: string;
  /** Hex id of the study directly above this one. */
  adminStudyId: string;
  /** 1 for a study directly below the admin study asked about. */
  depth: number;
  /** Archived and deleted cases out, locked ones in. */
  caseCount: number;
  /** In the child study's form order. */
  forms: ChildStudyForm[];
  /** False when the study has no `entryCompletionLogic`, so no record says whether it is complete. */
  completionMeasured: boolean;
  /** Whether the study has a finish rule, which only PUMP studies set (CONTEXT.md, Finished case). */
  finishMeasured: boolean;
  /** Null when `finishMeasured` is false. */
  finished: ChildStudyCaseSplit | null;
  /** Null when `finishMeasured` is false. With `finished`, every case `caseCount` counts. */
  active: ChildStudyCaseSplit | null;
  /** Locked cases among those `caseCount` counts. */
  lockedCount: number;
  /** Archived cases, which `caseCount` leaves out. */
  archivedCount: number;
  /** Complete records over every case and form; null when completion is not measured. */
  completeRecords: number | null;
  /** Records with an entry over every case and form; null when completion is not measured. */
  startedRecords: number | null;
}

export interface ChildStudies {
  adminStudyId: string;
  /** Nearest first, then by title. Training studies and everything below them are left out. */
  childStudies: ChildStudy[];
}

const mapForm = ({ id, title, entryCount, casesWithEntry, casesComplete }: ChildStudyForm): ChildStudyForm =>
  ({ id, title, entryCount, casesWithEntry, casesComplete });

/** A split the read sent as null stays null, so the section can tell "not measured" from 0. */
const mapSplit = (split: ChildStudyCaseSplit | null): ChildStudyCaseSplit | null =>
  split && { all: split.all, incomplete: split.incomplete };

const mapChildStudy = (study: ChildStudy): ChildStudy => ({
  id: study.id,
  title: study.title,
  adminStudyId: study.adminStudyId,
  depth: study.depth,
  caseCount: study.caseCount,
  forms: study.forms.map(mapForm),
  completionMeasured: study.completionMeasured,
  finishMeasured: study.finishMeasured,
  finished: mapSplit(study.finished),
  active: mapSplit(study.active),
  lockedCount: study.lockedCount,
  archivedCount: study.archivedCount,
  completeRecords: study.completeRecords,
  startedRecords: study.startedRecords
});

export const mapChildStudies = (response: ChildStudies): ChildStudies => ({
  adminStudyId: response.adminStudyId,
  childStudies: response.childStudies.map(mapChildStudy)
});

/** A field a form's completion counts, by id and label. */
export interface ChildStudyCountedField {
  id: string;
  label: string;
}

/** One of a child study's forms, with the fields its completion counts. */
export interface ChildStudyCasesForm {
  id: string;
  title: string;
  /** Archived fields out; empty when completion is not measured. */
  fields: ChildStudyCountedField[];
}

/** A counted field one or more of a case's entries on a form leave unanswered (CONTEXT.md, Outstanding field). */
export interface ChildStudyOutstandingField {
  /** One of the form's `fields` ids. */
  fieldId: string;
  /** How many of the case's entries on the form leave it unanswered. */
  missingIn: number;
  /** How many entries the case has on the form. */
  entries: number;
}

/**
 * A case against a form it has an entry on: the counted fields answered out of those counted, summed
 * over the case's entries on the form (CONTEXT.md, Completion record). Both null, and nothing
 * outstanding, when completion is not measured.
 */
export interface ChildStudyCompletionRecord {
  answered: number | null;
  total: number | null;
  /** Each field once, in the form's `fields` order. */
  outstanding: ChildStudyOutstandingField[];
}

export interface ChildStudyCaseRecords {
  /** The generated part of the case's name, or null when there is none. */
  name: string | null;
  /** Null when the study has no finish rule. */
  finished: boolean | null;
  locked: boolean;
  /** One per form, in `forms` order: null for no entry. */
  records: Array<ChildStudyCompletionRecord | null>;
}

/** One child study's cases, every one of them and in no guaranteed order. */
export interface ChildStudyCases {
  childStudyId: string;
  completionMeasured: boolean;
  finishMeasured: boolean;
  forms: ChildStudyCasesForm[];
  cases: ChildStudyCaseRecords[];
}

const mapCasesForm = ({ id, title, fields }: ChildStudyCasesForm): ChildStudyCasesForm =>
  ({ id, title, fields: fields.map(field => ({ id: field.id, label: field.label })) });

const mapRecord = (record: ChildStudyCompletionRecord | null): ChildStudyCompletionRecord | null =>
  record && {
    answered: record.answered,
    total: record.total,
    outstanding: record.outstanding.map(({ fieldId, missingIn, entries }) => ({ fieldId, missingIn, entries }))
  };

const mapCaseRecords = ({ name, finished, locked, records }: ChildStudyCaseRecords): ChildStudyCaseRecords =>
  ({ name, finished, locked, records: records.map(mapRecord) });

export const mapChildStudyCases = (response: ChildStudyCases): ChildStudyCases => ({
  childStudyId: response.childStudyId,
  completionMeasured: response.completionMeasured,
  finishMeasured: response.finishMeasured,
  forms: response.forms.map(mapCasesForm),
  cases: response.cases.map(mapCaseRecords)
});
